import React, { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  alpha,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import DashboardIcon from '@mui/icons-material/Dashboard'
import BusinessIcon from '@mui/icons-material/Business'
import PublicIcon from '@mui/icons-material/Public'
import PersonIcon from '@mui/icons-material/Person'
import AssignmentIcon from '@mui/icons-material/Assignment'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PeopleIcon from '@mui/icons-material/People'
import SettingsIcon from '@mui/icons-material/Settings'
import LogoutIcon from '@mui/icons-material/Logout'
import SendIcon from '@mui/icons-material/Send'
import DescriptionIcon from '@mui/icons-material/Description'
import RateReviewIcon from '@mui/icons-material/RateReview'
import SyncIcon from '@mui/icons-material/Sync'
import BarChartIcon from '@mui/icons-material/BarChart'
import StarIcon from '@mui/icons-material/Star'
import ArticleIcon from '@mui/icons-material/Article'
import MailIcon from '@mui/icons-material/Mail'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'

const drawerWidth = 280

// Brand color constants - matching find-bedemand.dk
const brand = {
  violet: '#5c4b99',
  navy: '#2d2855',
  lavender: '#f5f4f9',
  lavenderLight: '#faf9fc',
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [performanceVisible, setPerformanceVisible] = useState(true) // Default to visible
  const navigate = useNavigate()
  const location = useLocation()
  const { isAdmin } = useAuth()

  // Load performance visibility setting
  useEffect(() => {
    const loadPerformanceVisibility = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'performance_visible')
          .single()

        if (!error && data) {
          setPerformanceVisible(data.value === true || data.value === 'true')
        } else if (error) {
          // Table might not exist yet - that's okay, use default value
          console.log('app_settings table not found or no data, using default visibility')
        }
      } catch (err) {
        console.log('Error loading performance visibility (table may not exist yet):', err)
        // Keep default value (true) on error - this is fine
      }
    }

    loadPerformanceVisibility()

    // Set up real-time subscription to settings changes (only if table exists)
    let channel: any = null
    try {
      channel = supabase
        .channel('app_settings_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'app_settings',
            filter: 'key=eq.performance_visible',
          },
          (payload) => {
            if (payload.new && 'value' in payload.new) {
              setPerformanceVisible(payload.new.value === true || payload.new.value === 'true')
            }
          }
        )
        .subscribe()
    } catch (err) {
      console.log('Could not subscribe to settings changes (table may not exist yet)')
    }

    return () => {
      if (channel) {
        supabase.removeChannel(channel)
      }
    }
  }, [])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  // Check if current path matches
  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  // Styled list item with active state
  const NavItem = ({ 
    path, 
    icon, 
    label, 
    onClick 
  }: { 
    path?: string
    icon: React.ReactNode
    label: string
    onClick?: () => void 
  }) => {
    const active = path ? isActive(path) : false
    return (
      <ListItem disablePadding sx={{ px: 1.5, py: 0.25 }}>
        <ListItemButton 
          onClick={onClick || (() => path && navigate(path))}
          sx={{
            borderRadius: 2,
            py: 1.25,
            px: 1.5,
            backgroundColor: active ? alpha(brand.violet, 0.08) : 'transparent',
            borderLeft: active ? `2px solid ${brand.violet}` : '2px solid transparent',
            '&:hover': {
              backgroundColor: active ? alpha(brand.violet, 0.1) : alpha(brand.violet, 0.04),
            },
          }}
        >
          <ListItemIcon sx={{ 
            minWidth: 36,
            color: active ? brand.violet : '#7a7a7a',
            transition: 'color 0.15s ease',
          }}>
            {icon}
          </ListItemIcon>
          <ListItemText 
            primary={label}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: active ? 500 : 400,
              color: active ? brand.navy : '#5a5a5a',
            }}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  const drawer = (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: `linear-gradient(180deg, ${brand.lavenderLight} 0%, ${brand.lavender} 100%)`,
    }}>
      {/* Logo / Brand */}
      <Box sx={{ 
        px: 2, 
        py: 2.5,
        borderBottom: '1px solid rgba(92,75,153,0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Find Bedemand"
            sx={{
              height: 32,
              width: 'auto',
            }}
          />
          {isAdmin && (
            <Chip 
              label="Admin" 
              size="small" 
              sx={{
                backgroundColor: brand.violet,
                color: '#fff',
                fontWeight: 500,
                fontSize: '0.7rem',
                height: 22,
                borderRadius: '6px',
              }}
            />
          )}
        </Box>
      </Box>
      
      {/* Main Navigation */}
      <List sx={{ pt: 2, pb: 1 }}>
        {isAdmin && (
          <NavItem path="/" icon={<DashboardIcon sx={{ fontSize: 20 }} />} label="Dashboard" />
        )}
        {isAdmin && (
          <NavItem path="/undertakers" icon={<BusinessIcon sx={{ fontSize: 20 }} />} label="Bedemænd" />
        )}
        {isAdmin && (
          <NavItem path="/locations" icon={<PublicIcon sx={{ fontSize: 20 }} />} label="Lokationer" />
        )}
        {isAdmin && (
          <NavItem path="/articles" icon={<ArticleIcon sx={{ fontSize: 20 }} />} label="Artikler" />
        )}
        <NavItem path="/claims" icon={<AssignmentIcon sx={{ fontSize: 20 }} />} label="Mine forretninger" />
        {/* Show Performance menu if: user is admin OR performance is set to visible */}
        {(isAdmin || performanceVisible) && (
          <NavItem path="/performance" icon={<BarChartIcon sx={{ fontSize: 20 }} />} label="Performance" />
        )}
      </List>
      
      {/* Admin Section */}
      {isAdmin && (
        <>
          <Divider sx={{ mx: 2.5, my: 1, borderColor: 'rgba(92,75,153,0.08)' }} />
          <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: alpha(brand.violet, 0.5),
                fontWeight: 600,
                fontSize: '0.68rem',
                letterSpacing: '0.1em',
              }}
            >
              ADMINISTRATION
            </Typography>
          </Box>
          <List sx={{ pt: 0.5, pb: 1 }}>
            <NavItem path="/admin/approve" icon={<CheckCircleIcon sx={{ fontSize: 20 }} />} label="Godkend bedemænd" />
            <NavItem path="/admin/claims" icon={<AdminPanelSettingsIcon sx={{ fontSize: 20 }} />} label="Administrer claims" />
            <NavItem path="/admin/users" icon={<PeopleIcon sx={{ fontSize: 20 }} />} label="Administrer brugere" />
            <NavItem path="/admin/services-template" icon={<DescriptionIcon sx={{ fontSize: 20 }} />} label="Service-skabelon" />
            <NavItem path="/admin/services-submissions" icon={<SendIcon sx={{ fontSize: 20 }} />} label="E-mail System" />
            <NavItem path="/admin/submission-reviews" icon={<RateReviewIcon sx={{ fontSize: 20 }} />} label="Gennemse indsendelser" />
            <NavItem path="/admin/email-templates" icon={<DescriptionIcon sx={{ fontSize: 20 }} />} label="Email Skabeloner" />
            <NavItem path="/admin/bulk-update" icon={<SyncIcon sx={{ fontSize: 20 }} />} label="Bulk Opdatering" />
            <NavItem path="/admin/reviews" icon={<StarIcon sx={{ fontSize: 20 }} />} label="Google Anmeldelser" />
            <NavItem path="/admin/messages" icon={<MailIcon sx={{ fontSize: 20 }} />} label="Henvendelser" />
          </List>
        </>
      )}
      
      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />
      
      {/* User Section - pushed to bottom */}
      <Box sx={{ 
        borderTop: '1px solid rgba(92,75,153,0.06)',
        pt: 1,
        pb: 2,
      }}>
        <List sx={{ py: 0 }}>
          <NavItem path="/profile" icon={<PersonIcon sx={{ fontSize: 20 }} />} label="Profil" />
          {isAdmin && (
            <NavItem path="/settings" icon={<SettingsIcon sx={{ fontSize: 20 }} />} label="Indstillinger" />
          )}
          <ListItem disablePadding sx={{ px: 1.5, py: 0.25 }}>
            <ListItemButton 
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                py: 1.25,
                px: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(196, 92, 92, 0.06)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: '#9a9a9a' }}>
                <LogoutIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Log ud"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 400,
                  color: '#7a7a7a',
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: brand.lavender }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(92,75,153,0.06)',
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: brand.navy,
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              color: brand.navy,
              fontWeight: 500,
            }}
          >
            {isAdmin ? 'Admin Panel' : 'Find Bedemand'}
          </Typography>
          <Button 
            onClick={handleLogout}
            sx={{ 
              color: '#7a7a7a',
              '&:hover': {
                backgroundColor: alpha(brand.violet, 0.04),
                color: brand.navy,
              },
            }}
          >
            Log ud
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              borderRight: '1px solid rgba(92,75,153,0.06)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: brand.lavender,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  )
}

